import { orderBy, maxBy, get, uniq } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';
import { getName } from 'modules/appointmentInfo/utils/participantInfo';
import { ONGOING } from 'modules/common/constants/appointmentStatuses';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import * as appointmentStatuses from 'modules/common/constants/appointmentStatuses';
import { CUSTOMER, PROVIDER } from 'modules/common/constants/conversationParticipantsTypes';
import { FACE_TO_FACE, VIDEO, AUDIO_EXTERNAL } from 'modules/queues/constants/conversationTypes';
import { appointmentHavePermission, makeAppointmentReadPermissions } from 'modules/common/selectors/permissions';
import {
  DECLINED,
  UNANSWERED,
  IN_PROGRESS,
  endedConversationStatuses,
} from 'modules/common/constants/conversationStatuses';

import {
  APPOINTMENTS,
  APPOINTMENTS_PROVIDER,
  APPOINTMENTS_STATUS_CANCELED,
} from 'modules/common/constants/appointmentPermissionResources';


const getTranslations = state => state.common.apiTranslations.translations;
const isCurrentAppointmentSelected = state => !!state.appointmentInfo.currentAppointment.appointmentId;
export const getCurrentAppointment = state => state.appointmentInfo.currentAppointment;
export const isF2F = (state) => state.appointmentInfo.currentAppointment.allowedConversationTypes[0] === FACE_TO_FACE;
export const isCurrentAppointmentOngoing = state => state.appointmentInfo.currentAppointment.status === ONGOING;
export const currentAppointmentHavePermissions = (neededPermissions, neededServiceConfig) => createSelector(
  getCurrentAppointment,
  currentAppointment => appointmentHavePermission(currentAppointment, neededPermissions, neededServiceConfig),
);

export const currentConversation = state =>
  state.conference.currentConversation.requestStatus === requestsStatuses.success
    ? state.conference.currentConversation
    : maxBy(state.appointmentInfo.currentAppointment.conversations, 'conversationId') || { statusHistory: [] };

export const isClosedAppointment = createSelector(
  getCurrentAppointment,
  (appointment) => appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status),
);

export const canCloseAppointment = createSelector(
  isCurrentAppointmentSelected,
  isClosedAppointment,
  (isCurrentAppointmentSelected, isClosedAppointment) => !isClosedAppointment && isCurrentAppointmentSelected,
);

export const closeAppointmentPermissions = createStructuredSelector({
  hasCancelPermission: currentAppointmentHavePermissions({ [APPOINTMENTS_STATUS_CANCELED]: ['update'] }),
  canUpdateAppointment: currentAppointmentHavePermissions({ [APPOINTMENTS]: ['update'] }),
});

export const getCloseAppointmentPermissions = (state) => {
  const { hasCancelPermission, canUpdateAppointment } = closeAppointmentPermissions(state);

  return {
    hasCancelPermission: hasCancelPermission && canUpdateAppointment,
    canUpdateAppointment,
  };
};

export const isCurrentConversationMCU = createSelector(
  getCurrentAppointment,
  appointment => appointment.service.config.multiParticipantCallEnabled,
);

export const getCurrentUserId = state => state.auth.userInfo.userId;

export const getReadCurrentAppointmentPermissions = createSelector(
  getCurrentAppointment,
  makeAppointmentReadPermissions,
);

export const currentConversationStatus = (state) => {
  const curConversation = currentConversation(state);
  return curConversation && curConversation.status;
};

export const getAppointmentCustomer = createSelector(
  getCurrentAppointment,
  appointment =>
    appointment.participants
      .find((participant) => participant.participantType === CUSTOMER).user);

export const getAppointmentProvider = createSelector(
  getCurrentAppointment,
  appointment =>
    appointment.participants
      .find((participant) => participant.participantType === PROVIDER).user);


export const getConversationCustomer = createSelector(
  currentConversation,
  getCurrentUserId,
  conversation => {
    const customer = conversation.participants && conversation.participants
      .find((participant) => participant?.participantType === CUSTOMER);
    return get(customer, 'user', null);
  });

export const getConversationProvider = createSelector(
  currentConversation,
  getCurrentUserId,
  conversation => {
    const provider = conversation.participants && conversation?.participants
      .find((participant) => participant?.participantType === PROVIDER);
    return get(provider, 'user', null);
  },
);

export const isCurrentUserConversationCreator = createSelector(
  currentConversation,
  getCurrentUserId,
  (conversation, userId) =>
    conversation.createdBy === userId,
);

export const isCurrentConversationSIP = createSelector(
  currentConversation,
  conversation => AUDIO_EXTERNAL === conversation.type,
);

export const getConversationParticipant = createSelector(
  getConversationProvider,
  getConversationCustomer,
  getCurrentUserId,
  (provider, customer, userId) =>
    userId === customer?.userId
      ? provider && provider?.profile
      : customer?.profile,
);

export const getConversationParticipantName = createSelector(
  getConversationParticipant,
  currentAppointmentHavePermissions({ [APPOINTMENTS_PROVIDER]: ['read'] }),
  (participant, canSeeProvider) => canSeeProvider ? getName(participant) : '*****',
);

export const isConversationParticipant = createSelector(
  currentConversation,
  getCurrentUserId,
  (conversation, userId) => {
    return get(conversation, 'participants', [])
      .some(participant => get(participant, 'user.userId') === userId);
  },
);

export const isMissingFiles = createSelector(
  getCurrentAppointment,
  appointment =>
    appointment.filePrerequisiteStatus === 'INCOMPLETE' &&
    !appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status),
);

export const isMaxUploadsReached = createSelector(
  getCurrentAppointment,
  appointment =>
    appointment.filePrerequisiteStatus === 'REACH_MAX_UPLOADED_FILES' &&
    !appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status),
);


export const getMissingFilesMessage = createSelector(
  getCurrentAppointment,
  isMissingFiles,
  getTranslations,
  (appointment, isMissingFiles, translations) => {
    if (!isMissingFiles) return null;

    return get(translations.find(item => item.key === 'FILES_PREREQUISITES_NOTIFICATION_MESSAGE'), 'value', '');
  },
);

export const getMaxFilesReachedMessage = createSelector(
  getCurrentAppointment,
  isMaxUploadsReached,
  getTranslations,
  (appointment, isMaxUploadsReached, translations) => {
    if (!isMaxUploadsReached) return null;

    return get(translations.find(item => item.key === 'FILES_PREREQUISITES_MAX_UPLOADED_MESSAGE'), 'value', '');
  },
);

export const isConversationInProgressStatus = createSelector(
  getCurrentAppointment,
  ({ conversations }) => {
    const [lastConversation] = orderBy(conversations, ['conversationId'], ['desc']);

    return lastConversation && lastConversation.status && lastConversation.status === IN_PROGRESS;
  },
);

export const isCurrentAppointmentGrouped = createSelector(
  getCurrentAppointment,
  ({ group }) => !!group,
);

export const getMissingCaseMessage = createSelector(
  [getCurrentAppointment],
  (appointment) => {
    const enabled = get(appointment, 'case.casePrerequisite.status') === 'INCOMPLETE' &&
      !appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status);

    if (!enabled || !appointment.case.casePrerequisite.errorMessage) return;

    return appointment.case.casePrerequisite.errorMessage;
  },
);

export const isFullPrerequisiteIncomplete = createSelector(
  [getCurrentAppointment],
  (appointment) => {
    const casePrerequisitesIncomplete = get(appointment, 'case.casePrerequisite.status') === 'INCOMPLETE';
    const filePrerequisitesIncomplete = appointment?.filePrerequisiteStatus === 'INCOMPLETE' && get(appointment, 'service.filesPrerequisitesConfig.enabled');

    return casePrerequisitesIncomplete || filePrerequisitesIncomplete;
  },
);

const isCallInProgress = (state) => state.conference.consultationInfo.startConsultationRequestStatus === requestsStatuses.success


export const isCallTypeSelectionEnabled = createSelector(
  getCurrentAppointment,
  state => state.appointmentInfo.currentAppointment?.service?.conversationTypes,
  state => state.appointmentInfo.currentAppointment?.allowedConversationTypes,
  state => state.appointmentInfo.currentAppointment?.service?.config?.conversationTypeSelectionEnabled,
  ({ conversations }, appConversationTypes, allowedConversationTypes, conversationTypeSelectionEnabled) => {
    const appointmentHasExpectedConversation = conversations.some((conversation) => {
      const { status, initiator } = conversation;
      return endedConversationStatuses.includes(status) || ([UNANSWERED, DECLINED].includes(status) && initiator !== 'DIALER');
    });
    return appointmentHasExpectedConversation &&
      conversationTypeSelectionEnabled &&
      uniq([...allowedConversationTypes, ...appConversationTypes]).includes(AUDIO_EXTERNAL) &&
      uniq([...allowedConversationTypes, ...appConversationTypes]).includes(VIDEO);
  },
);

export const isCurrentConversationVideo = createSelector(
  getCurrentAppointment,
  isCurrentConversationSIP,
  isClosedAppointment,
  isCallInProgress,
  isCallTypeSelectionEnabled,
  (
    appointment,
    isCurrentConversationSIP,
    isClosedAppointment,
    conferenceInProgress,
    callTypeSelectionEnabled,
  ) => {
    const plannedConversationType = appointment?.allowedConversationTypes[0];

    return (plannedConversationType === VIDEO
        || (callTypeSelectionEnabled && [VIDEO, AUDIO_EXTERNAL].includes(plannedConversationType)))
      && !(isCurrentConversationSIP && conferenceInProgress) && !isClosedAppointment;
  },
);
